//import "@mui/lab";
import { useTheme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import NoData from "../../components/no-data/NoData";
import { LayoutContextActionType } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useSearchResults } from "../../queries/search-results/UseSearchResults";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ISearchResultsItem, ISearchResultsItemsGroup } from "../../services/object-search-data-service/Models";
import SearchViewItemList from "./SearchViewItemList";

export type SearchViewContentProps = {};

export default function SearchViewContent(_: SearchViewContentProps) {

    const [, layoutDispatch] = useLayoutContext();
    const routeContext = useRouteContext();
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();

    const searchText = getSearchText();

    const { data: searchResults, status } = useQueryNotifications(useSearchResults)({
        groupID: routeContext?.groupID as number,
        searchText: searchText ?? ""
    });

    function getSearchText(): string | undefined {
        if (location.search.length > 0) {

            const params = new URLSearchParams(location.search);

            if (params.has("token")) {
                const text = params.get("token");
                if (text && text.length) {
                    return text;
                }
            }
        }

        return undefined;
    }

    const containerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {

        // set our scroll container as the scroll target so the toolbar at top of view
        // will scroll out of view
        if (containerRef?.current) {
            layoutDispatch({
                type: LayoutContextActionType.ScrollTargetSet,
                scrollTarget: containerRef.current
            });
        }
    }, [containerRef,
        layoutDispatch]);

    return (
        // NOTE using div instead of Box because box does not allow ref prop and we
        // need an HTMLElement to set as scroll target if we want the toolbar area to scroll out
        // of view when the contents of this view scroll.

        (<LoadingZone isLoading={status === "loading"}>
            <div
                style={{
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                    overflow: 'hidden',
                    overflowY: 'auto',
                }}
                ref={containerRef}
            >


                {searchResults && !searchResults?.groupsSection.items.length &&
                    !searchResults?.alarmLynxsSection.groups.length &&
                    !searchResults?.compressorLynxsSection.groups.length &&
                    !searchResults?.wellsSection.groups.length &&
                    !searchResults?.polledDevicesSection.groups.length ?

                    <NoData title={t('search.nodataTitle')} text={t('search.nodata')} />
                    :
                    null
                }

                {(searchResults?.groupsSection.items.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.groupsContaining')}
                        subHeader={t('search.groups')}
                        searchText={searchText as string}
                        items={[{
                            label: undefined,
                            items: searchResults?.groupsSection.items as ISearchResultsItem[]
                        }]} />
                }

                {(searchResults?.alarmLynxsSection.groups.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.alarmlynx')}
                        subHeader={t('search.membership')}
                        searchText={searchText as string}
                        items={searchResults?.alarmLynxsSection.groups as ISearchResultsItemsGroup[]} />
                }

                {(searchResults?.compressorLynxsSection.groups.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.compressorlynx')}
                        subHeader={t('search.membership')}
                        searchText={searchText as string}
                        items={searchResults?.compressorLynxsSection.groups as ISearchResultsItemsGroup[]} />
                }

                {(searchResults?.polledDevicesSection.groups.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.devices')}
                        subHeader={t('search.membership')}
                        searchText={searchText as string}
                        items={searchResults?.polledDevicesSection.groups as ISearchResultsItemsGroup[]} />
                }


                {(searchResults?.wellsSection.groups.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.wells')}
                        subHeader={t('search.membership')}
                        searchText={searchText as string}
                        items={searchResults?.wellsSection.groups as ISearchResultsItemsGroup[]} />
                }

                {(searchResults?.tankBatterySection.groups.length ?? 0) > 0 &&

                    <SearchViewItemList header={t('search.tankBatteries')}
                        subHeader={t('search.membership')}
                        searchText={searchText as string}
                        items={searchResults?.tankBatterySection.groups as ISearchResultsItemsGroup[]} />
                }


            </div>
        </LoadingZone>)
    );
}

